import {
  AudiosGetOrderByCreationTimeEnum,
  AudiosGetOrderByDurationEnum,
  AudiosGetOrderByNameEnum,
  AudiosGetOrderByStartTimeEnum,
  AudiosGetOrderByStateEnum,
  AudiosGetOrderByTimeEnum,
  AudiosGetRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { createRoute } from '@tanstack/react-router'
import { t } from 'i18next'

import { StaticRouteParameter } from 'config/routing'
import { Overview } from 'domains/Workspaces/Audio/Objects'
import { audioRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Routing'
import { Structure } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Structure'

export const audioObjectsRoute = createRoute({
  getParentRoute: () => audioRoute,
  path: StaticRouteParameter.OBJECTS,
  loader: () => ({
    crumb: t('entity:content.audioObject', { count: 2 }),
  }),
})

export const audioObjectsLayoutRoute = createRoute({
  getParentRoute: () => audioObjectsRoute,
  id: 'audioObjectLayout',
  component: Structure,
})

type AudioObjectsOverviewSearch = {
  query?: string
  groups?: string[]
  excludeGrouped?: boolean
} & Partial<PickStartsWith<AudiosGetRequest, 'timeRange'>> &
  Partial<PickStartsWith<AudiosGetRequest, 'orderBy'>>

const sortProperties = [
  'orderByTime',
  'orderByName',
  'orderByDuration',
  'orderByState',
  'orderByStartTime',
  'orderByCreationTime',
] as const satisfies (keyof PickStartsWith<AudiosGetRequest, 'orderBy'>)[]

const sortPropertyValues = new Map<keyof PickStartsWith<AudiosGetRequest, 'orderBy'>, Record<string, string>>([
  ['orderByTime', AudiosGetOrderByTimeEnum],
  ['orderByName', AudiosGetOrderByNameEnum],
  ['orderByDuration', AudiosGetOrderByDurationEnum],
  ['orderByState', AudiosGetOrderByStateEnum],
  ['orderByStartTime', AudiosGetOrderByStartTimeEnum],
  ['orderByCreationTime', AudiosGetOrderByCreationTimeEnum],
])

export const audioObjectsOverviewRoute = createRoute({
  getParentRoute: () => audioObjectsLayoutRoute,
  path: '/',
  component: () => <Overview />,
  validateSearch: (search: Record<string, unknown>): AudioObjectsOverviewSearch => {
    const result: Record<string, unknown> = {}

    if (search.query && typeof search.query === 'string' && !!search.query.length) {
      result.query = search.query
    }

    if (search.groups && typeof search.groups === 'string' && !!search.groups.length) {
      result.groups = search.groups
    }

    if (search.excludeGrouped && typeof search.excludeGrouped === 'string' && !!search.excludeGrouped.length) {
      result.excludeGrouped = search.excludeGrouped
    }

    if (search.timeRangeStart && typeof search.timeRangeStart === 'number' && !isNaN(search.timeRangeStart)) {
      result.timeRangeStart = search.timeRangeStart
    }

    if (search.timeRangeEnd && typeof search.timeRangeEnd === 'number' && !isNaN(search.timeRangeEnd)) {
      result.timeRangeEnd = search.timeRangeEnd
    }

    for (const sortProperty of sortProperties) {
      if (
        search[sortProperty] &&
        typeof search[sortProperty] === 'string' &&
        search[sortProperty] in sortPropertyValues.get(sortProperty)!
      ) {
        result[sortProperty] = search[sortProperty]
      }
    }

    return result satisfies AudioObjectsOverviewSearch
  },
})
